import { queryOptions } from '@tanstack/react-query'

import { knockOutPublicFetcher } from '@/libs/fetcher/presets'

import type { PredictionNavNode } from './types'

export const predictionNavTreeQueryKey = ['prediction', 'navigation-tree'] as const

// ── Tree helpers (ported from cdc-web services/knockOuts/utils/navigationTree.ts)
// URL-resolving helpers (resolveChipHref / isChipActive / resolveSectionHref)
// are cut: the prediction section uses local selection state, not URL sync.

/**
 * Walk the nav tree by `path` (slug chain like `/sports/soccer/epl`).
 * Returns the matching node, or `undefined` if not found.
 *
 * Empty `path` ('' or '/') returns `undefined` — caller should fall back to
 * the root-level (e.g. the first L1 chip).
 */
export const findNodeByPath = (
  nodes: PredictionNavNode[] | undefined,
  path: string,
): PredictionNavNode | undefined => {
  if (!nodes?.length) return undefined
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (normalized === '/') return undefined

  for (const node of nodes) {
    if (node.path === normalized) return node
    const found = findNodeByPath(node.children, normalized)
    if (found) return found
  }
  return undefined
}

/**
 * BFS 找树中第一个 slug 匹配的节点。
 * Slugs 在树中不严格唯一,调用方应传预期匹配顶层偏上的 slug;BFS 偏向最高匹配。
 */
export const findNodeBySlug = (
  nodes: PredictionNavNode[] | undefined,
  slug: string,
): PredictionNavNode | undefined => {
  if (!nodes?.length) return undefined
  const queue: PredictionNavNode[] = [...nodes]
  while (queue.length > 0) {
    const node = queue.shift()!
    if (node.slug === slug) return node
    if (node.children?.length) queue.push(...node.children)
  }
  return undefined
}

/**
 * Walk the nav tree for the node whose `path` matches `childPath`, then return
 * its parent. Used to walk a scope/[F+D]/duration overlay leaf back up to the
 * category that owns it.
 */
export const findParentByPath = (
  nodes: PredictionNavNode[] | undefined,
  childPath: string,
): PredictionNavNode | undefined => {
  if (!nodes?.length) return undefined
  for (const node of nodes) {
    if (node.children?.some(child => child.path === childPath)) return node
    const found = findParentByPath(node.children, childPath)
    if (found) return found
  }
  return undefined
}

/** BE 表示 folder 的 catch-all 目的地目标时用的 slug。 */
const ALL_DESTINATION_SLUG = 'all'

/**
 * L1 导航条的根节点过滤（port 自 cdc-web `filterL1NavRoots`，最终源于
 * monaco-app `usePredictCategoryNav`）：
 * 1. 排除根级 `scope` 节点（Games/Futures 是内容区 tab，不是一级导航）；
 * 2. 排除 placeholder sentinel —— BE 用 `sentinel: true` + `filter.tags:
 *    'placeholder'` 标记未上线、无事件 feed 的 hub 节点（实测 Pro Football
 *    Hub 就是这种）。trending/live 真 sentinel 的 filter 是
 *    `{trending:'true'}` / `{live:'true'}`，不含 placeholder tag，不受影响；
 * 3. 排除 0 事件的分类 —— count 取节点自身 `event_count`，缺失时回退到其
 *    `all` 子节点的 count；两者都缺失时保留（BE 不总能预算 count）。
 */
export const filterL1NavRoots = (nodes: PredictionNavNode[] | undefined): PredictionNavNode[] =>
  (nodes ?? [])
    .filter(n => n.node_type !== 'scope')
    .filter(n => !(n.sentinel === true && n.filter?.tags === 'placeholder'))
    .filter(n => {
      const count =
        n.event_count ?? n.children?.find(c => c.slug === ALL_DESTINATION_SLUG)?.event_count
      return count === undefined || count > 0
    })

/**
 * 把 nav-tree 的 `icon_url` 按 `filter.event_kind_groups` 映射到组 id（port 自
 * cdc-web `buildEventKindGroupIconMap`）——**仅供 sports 事件解析**（预测类
 * 分类在树里是 per-token 兄弟文件夹、map 构造性歧义，调用方见
 * PredictionSection `resolveIconSrc` 的分叉）。带 parent-folder 继承：子节点
 * 没有自己 icon 时用祖先的（IPL / WOLY / Tennis-ATP-WTA 等"比分类"都靠这个
 * 覆盖，be 的 icon_url 只在部分 folder 上显式下发）。
 *
 * cdc-web 同款还有个 `node.destination.event_kind_group.id` 环；该字段已
 * @deprecated（用 filter 代替）、且 cdc-market 的 PredictionNavNode 无此
 * 字段，故省。First writer wins：最深的节点后访问，但其 icon 与文件夹祖先
 * 继承一致，只是让 map 保持稳定。
 */
export const buildEventKindGroupIconMap = (
  nodes: PredictionNavNode[] | undefined,
): Map<string, string> => {
  const map = new Map<string, string>()
  if (!nodes) return map
  const visit = (node: PredictionNavNode, inheritedIcon: string | undefined) => {
    const icon = node.icon_url || inheritedIcon
    if (icon) {
      const groups = node.filter?.event_kind_groups
      if (groups) {
        for (const id of groups.split(',')) {
          const trimmed = id.trim()
          if (trimmed && !map.has(trimmed)) map.set(trimmed, icon)
        }
      }
    }
    node.children?.forEach(child => visit(child, icon))
  }
  nodes.forEach(node => visit(node, undefined))
  return map
}

/**
 * Resolve the filter a folder node should use when it acts as a destination.
 * Implements PRD §4 `[F+D]` (Folder + Destination) — the folder is also a
 * landing page that returns "everything inside the folder", not just an
 * expandable header.
 *
 * Resolution order:
 *   1. Folder's own filter — BE sets this on a small number of nodes. Use verbatim.
 *   2. `all` child's filter — BE emits an `all` destination as the first child
 *      of every `[F+D]` folder, carrying the union filter the folder wants.
 *   3. Synthesised EKG union — last resort, walks descendants and unions every
 *      `event_kind_groups` value.
 *
 * Returns `undefined` when the folder is genuinely grouping-only.
 */
export const synthesiseFolderFilter = (
  folder: PredictionNavNode,
): Record<string, string> | undefined => {
  if (folder.filter) return folder.filter

  const allChild = folder.children?.find(
    c => c.node_type === 'destination' && c.slug === ALL_DESTINATION_SLUG && c.filter,
  )
  if (allChild?.filter) return allChild.filter

  const ekgIds = collectDescendantEventKindGroups(folder)
  if (ekgIds.length > 0) {
    return { event_kind_groups: ekgIds.join(',') }
  }

  return undefined
}

/**
 * Recursively collect every `event_kind_groups` ID under `folder`'s subtree.
 * Skips `scope` nodes (those carry the parent's EKG filter plus an
 * `event_type` overlay — counting them would double-count IDs already
 * picked up from the parent destination).
 *
 * IDs are deduplicated to keep the resulting query string short and to
 * avoid surprising BE with redundant entries. Output is sorted so that two
 * trees with the same EKG set produce the same query string regardless of
 * how BE happened to lay out the children.
 */
function collectDescendantEventKindGroups(folder: PredictionNavNode): string[] {
  const seen = new Set<string>()
  const visit = (node: PredictionNavNode) => {
    if (node.node_type === 'scope') return
    const groups = node.filter?.event_kind_groups
    if (groups) {
      for (const id of groups.split(',')) {
        const trimmed = id.trim()
        if (trimmed) seen.add(trimmed)
      }
    }
    node.children?.forEach(visit)
  }
  folder.children?.forEach(visit)
  return Array.from(seen).sort()
}

interface NavTreeResponse {
  data: PredictionNavNode[]
}

const fetchNavigationTree = async (): Promise<PredictionNavNode[]> => {
  // successResponseDataProperty: '' keeps the raw {data} body — with the
  // fetcher's default unwrap `res` would already be the roots array and
  // `res.data` below would be undefined (repo pattern, see fetchCoins in
  // services/coin/coins.ts).
  const res = await knockOutPublicFetcher<NavTreeResponse>('/api/v1/navigation-tree', undefined, {
    successResponseDataProperty: '',
  })
  return res?.data ?? []
}

export function predictionNavTreeQueryOptions() {
  return queryOptions({
    queryKey: predictionNavTreeQueryKey,
    queryFn: fetchNavigationTree,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}
